import classnames from 'classnames';
import { Button } from '@material-ui/core';
import { computed, observable, action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { injectDependency, provide } from '@servicetitan/react-ioc';
import { EstimatesPageContainer } from '../estimates-page';
import { EstimateDetails } from '../../estimate-details/estimate-details';
import { EstimatesList } from '../../estimates-list/estimates-list';
import { PageError } from 'src/core/components/page-error/page-error';
import { PageFooterContainer } from 'src/core/components/page-footer/page-footer';
import { Stack } from '@servicetitan/design-system';
import { RoundArrow } from '../../round-arrow/round-arrow';
import { ApplicationDrawer } from '../../service-finance/application-drawer';
import { FinancingLoader } from '../../service-finance/desktop/financing-loader';

import styles from './estimates-page.less';
import { CustomerAddress } from 'src/api';

interface Props {
    container: EstimatesPageContainer;
}

interface State {
    total: number;
    isLoading: boolean;
}

@observer
export class EstimatesPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            total: 0,
            isLoading: false,
        };
    }

    private container = this.props.container;
    @observable
    private hideButton = true;

    @observable private isApplicationDrawerOpen = false;
    @action openDrawer = () => {
        this.isApplicationDrawerOpen = true;
        document.body.style.overflow = 'hidden';
    };

    @action
    closeDrawer = () => {
        this.isApplicationDrawerOpen = false;
        document.body.style.overflow = 'auto';
    };

    @observable private isIframeApplicationDrawerOpen = false;
    @action toggleIframeDrawer = () => {
        this.isIframeApplicationDrawerOpen = !this.isIframeApplicationDrawerOpen;
    };

    @computed private get showPrevButton() {
        const { estimates, selectedEstimate } = this.container;
        return estimates && selectedEstimate && estimates.indexOf(selectedEstimate) > 0;
    }
    @computed private get showNextButton() {
        const { estimates, selectedEstimate } = this.container;
        return (
            estimates &&
            selectedEstimate &&
            estimates.indexOf(selectedEstimate) < estimates.length - 1
        );
    }

    private financingRef: React.RefObject<HTMLIFrameElement> = React.createRef();

    componentDidMount(): void {
        window.addEventListener('message', (message) => {
            if (message.data.type === 'toggleDrawer') {
                runInAction(() => {
                    this.toggleIframeDrawer();
                });
            }

            if (
                message.data.type === 'openFinancingApplication' &&
                message.data?.estimateId &&
                this.financingRef.current
            ) {
                const contentWindow = this.financingRef.current.contentWindow;
                const estimateToOpen = this.container.estimates?.find(
                    (e) => e.id === message.data.estimateId
                );
                const total = estimateToOpen?.total ?? 0;
                this.setState({ total, isLoading: true }, () => {
                    setTimeout(() => {
                        contentWindow?.postMessage('openFinancingApplication', '*');
                        this.setState({ isLoading: false });
                    }, 3000);
                });
            }
        });
    }

    componentDidUpdate() {
        setTimeout(this.handleScroll);
    }

    private handleScroll = (event?: Event) => {
        // should display the accept button if page is short to fit in without scrolling
        if (window.scrollY < 400 && document.body.scrollHeight - window.innerHeight > 400) {
            this.hideButton = true;
        } else {
            this.hideButton = false;
        }
    };

    private handleButtonsRef = (ref: HTMLElement | null) => {
        (ref ? window.addEventListener : window.removeEventListener)('scroll', this.handleScroll);
        (ref ? window.addEventListener : window.removeEventListener)('resize', this.handleScroll);
    };

    private goPrev = () => {
        this.go(false);
    };

    private goNext = () => {
        this.go(true);
    };

    private go(next: boolean) {
        if (!this.container.estimates || !this.container.selectedEstimate) {
            return;
        }

        let index = this.container.estimates.indexOf(this.container.selectedEstimate);
        index = next ? index + 1 : index - 1;
        const estimate = this.container.estimates[index];
        if (estimate) {
            this.container.estimateStore.gotoEstimate(estimate.id);
        }
    }

    private getAddressString = (address: CustomerAddress) =>
        `Location: ${address.streetAddress}, ${address.unit ? `#${address.unit}, ` : ''} ${
            address.city
        }, ${address.state}, ${address.zip}`;

    render() {
        const {
            clientDataStore,
            estimates,
            selectedEstimate,
            errorMessage,
            clearError,
            gotoTerms,
            priceOptions,
            financingEnabled,
            gotoMobile,
            financingPresentationSetting,
            shouldShowApplyForFinancing,
            ldClient,
            financingProvider,
        } = this.container;

        const tenantId = selectedEstimate?.tenantId;
        const buId = selectedEstimate?.buId;
        const jobId = selectedEstimate?.jobId;
        const technicianId = selectedEstimate?.technicianId;
        const customer = selectedEstimate?.customer;
        const { total } = this.state;
        const stateCode = selectedEstimate?.stateCode;
        const customerEmail = clientDataStore.loginData.email;
        const sourceId = selectedEstimate?.id;
        const newMfe = ldClient?.variation('cp1-new-mfe', false) ?? false;
        const provider = financingProvider ?? '';

        return (
            <div className={classnames('page estimates-item-fill', styles.page)}>
                <div className="page-topping estimates-top-fill">
                    <PageError message={errorMessage} onClose={clearError} />
                    <Stack className={styles.titleContainer} alignItems="baseline">
                        {estimates && estimates.length > 0 && (
                            <div className={classnames('page-title', styles.pageTitle)}>
                                <h2>
                                    {estimates.length == 1
                                        ? 'Hello, this is your estimate '
                                        : `Hello, these are your ${
                                              estimates.length >= 4 ? estimates.length : ''
                                          } estimates `}

                                    {selectedEstimate?.customer?.address && (
                                        <span className={styles.subdued}>
                                            {this.getAddressString(
                                                selectedEstimate.customer.address
                                            )}
                                        </span>
                                    )}
                                </h2>
                            </div>
                        )}
                        {shouldShowApplyForFinancing ? (
                            <Button
                                onClick={this.openDrawer}
                                color="primary"
                                className={styles.financingApplicationButton}
                            >
                                Apply For Financing
                            </Button>
                        ) : (
                            selectedEstimate && (
                                <iframe
                                    ref={this.financingRef}
                                    src={`/financing/iframe?tenantId=${tenantId}&buId=${buId}&technicianId=${technicianId}&customerId=${
                                        customer?.id
                                    }&jobId=${jobId}&jobState=${stateCode}&customerFirstName=${
                                        customer?.firstName || ''
                                    }&customerLastName=${customer?.lastName || ''}&customerEmail=${
                                        customerEmail || ''
                                    }&estimateAmount=${total}&sourceId=${sourceId}&iframeMode=true&provider=${provider}&newMfe=${newMfe}`}
                                    className={
                                        this.isIframeApplicationDrawerOpen
                                            ? styles.financingApplication
                                            : styles.financingApplicationContainer
                                    }
                                />
                            )
                        )}
                        {selectedEstimate && (
                            <div className={styles.jobIdSection}>
                                <div className={styles.jobIdTitle}>
                                    {+selectedEstimate.jobId ? 'Job\xa0Id' : 'Project\xa0Id'}
                                </div>
                                <div className={styles.jobId}>
                                    {+selectedEstimate.jobId || selectedEstimate.projectId}
                                </div>
                            </div>
                        )}
                    </Stack>
                </div>

                {estimates ? (
                    <React.Fragment>
                        <EstimatesList
                            estimates={estimates}
                            selectedEstimate={selectedEstimate}
                            priceOptions={priceOptions}
                            financingEnabled={financingEnabled}
                            financingPresentationSetting={financingPresentationSetting}
                        />
                        {selectedEstimate && priceOptions !== undefined ? (
                            <EstimateDetails
                                estimate={selectedEstimate}
                                priceOptions={priceOptions}
                                shouldShowApplyForFinancing={shouldShowApplyForFinancing}
                                financingPresentationSetting={financingPresentationSetting}
                                gotoMobile={gotoMobile}
                            />
                        ) : null}
                    </React.Fragment>
                ) : null}

                <div className="space-filler"></div>
                <div
                    className={classnames(
                        'sticky-button-wrapper estimates-button-fill',
                        styles.buttons,
                        { [styles.inactive]: this.hideButton }
                    )}
                    ref={this.handleButtonsRef}
                >
                    <RoundArrow
                        className={styles.prev}
                        onClick={this.goPrev}
                        direction="left"
                        hidden={!this.showPrevButton}
                    />
                    {selectedEstimate && !selectedEstimate.isAccepted && (
                        <Button
                            className="next-step-button"
                            variant="contained"
                            color="primary"
                            onClick={gotoTerms}
                        >
                            Accept Estimate
                        </Button>
                    )}
                    <RoundArrow
                        className={styles.next}
                        onClick={this.goNext}
                        direction="right"
                        hidden={!this.showNextButton}
                    />
                </div>
                {this.state.isLoading && <FinancingLoader centerPage />}
                {this.isApplicationDrawerOpen && (
                    <ApplicationDrawer
                        closeCallback={this.closeDrawer}
                        selectedEstimate={this.container.selectedEstimate}
                    />
                )}
                <PageFooterContainer />
            </div>
        );
    }
}
